/**
 * In-app results list for the "auto services" screen. Replaces the legacy
 * "open in Google Maps / search in Google" flow — we now show actual results
 * directly inside the modal, with call + navigate actions per item.
 *
 * Renders into the existing `#autoActions` slot in the modal (the area below
 * the category grid). Keeps the user inside the app for browsing/filtering;
 * navigation tap opens the Maps app via a deep link, phone tap opens the
 * native dialer.
 */

import * as repo from '../../data/repositories/services.repo.js';
import { escape } from '../../shared/escape.js';
import { format as formatPhone } from '../../shared/phone.js';
import { formatDistance } from '../../shared/geo.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('autoServices.list');

const SLOT_ID = 'autoActions';

/** App-level category id (matches the legacy `data-term` attribute we already
 *  set on each `.auto-card` button — see `wireCategoryButtons` below). */
/** @typedef {import('../../data/repositories/services.repo.js').ServiceCategory} Category */

/** Friendly display for each category. */
const META = /** @type {Record<Category, { icon: string; title: string }>} */ ({
  gas:        { icon: '⛽', title: 'תחנות דלק' },
  garage:     { icon: '🔧', title: 'מוסכים מורשים' },
  inspection: { icon: '📋', title: 'מכוני בדיקה' },
  parking:    { icon: '🅿️', title: 'חניונים' },
  car_wash:   { icon: '🚿', title: 'רחיצות רכב' },
  car_parts:  { icon: '🏪', title: 'חנויות חלפים' },
});

/**
 * Map the legacy Hebrew `data-term` strings on the category buttons to
 * structured category ids. We pattern-match instead of doing exact equality
 * so a typo doesn't silently break the screen.
 *
 * Hebrew gotcha: the data-term on the legacy buttons uses the plural form
 * `"מוסכים"` (regular kaf, U+05DB), but the natural singular stem is
 * `"מוסך"` (final kaf, U+05DA). `includes('מוסך')` returns false against the
 * plural because the characters are different code points. We normalise both
 * sides by collapsing the 5 final-form letters (ך/ם/ן/ף/ץ → כ/מ/נ/פ/צ)
 * before substring-matching, so plural / singular / declined variants all
 * resolve correctly.
 *
 * @param {string} term
 * @returns {Category | null}
 */
export function termToCategory(term) {
  const t = normalizeHebrewFinals((term || '').trim());
  if (t.includes('דלק'))                          return 'gas';
  if (t.includes('מוסכ'))                         return 'garage';
  if (t.includes('בדיקה'))                        return 'inspection';
  if (t.includes('חני'))                          return 'parking';
  if (t.includes('רחיצ') || t.includes('שטיפ'))    return 'car_wash';
  if (t.includes('חלפ'))                          return 'car_parts';
  return null;
}

/** Replace each of the 5 Hebrew final-form letters with its regular form so
 *  substring comparisons aren't tripped up by the codepoint difference. */
function normalizeHebrewFinals(s) {
  return s
    .replace(/ך/g, 'כ')
    .replace(/ם/g, 'מ')
    .replace(/ן/g, 'נ')
    .replace(/ף/g, 'פ')
    .replace(/ץ/g, 'צ');
}

/**
 * Render a results list for the given category. Pulls user lat/lon and city
 * label from `window.__userLoc` / `getUserCityLabel` (set by the legacy
 * geolocation module — the EV-stations screen uses the same source).
 *
 * @param {Category} category
 */
export async function render(category) {
  const host = document.getElementById(SLOT_ID);
  if (!host) { log.warn('host #' + SLOT_ID + ' missing'); return; }

  const meta = META[category];
  host.innerHTML = headerHtml(meta) + `<div class="svc-list" aria-live="polite">${loadingHtml()}</div>`;
  const listEl = /** @type {HTMLElement} */ (host.querySelector('.svc-list'));

  const w = /** @type {any} */ (window);
  // The legacy `window.__userLoc` is a flat object — `{ lat, lon, latitude,
  // longitude, accuracy, timestamp, city? }` — NOT the raw `GeolocationPosition`.
  // Read both shapes defensively in case some code path stores the raw one.
  let lat = w.__userLoc?.lat ?? w.__userLoc?.latitude ?? w.__userLoc?.coords?.latitude;
  let lon = w.__userLoc?.lon ?? w.__userLoc?.longitude ?? w.__userLoc?.coords?.longitude;

  // No location yet (modal opened before the initial fix acquired GPS) — try
  // to acquire it now. Most categories rely on lat/lon to sort by distance.
  if ((!Number.isFinite(lat) || !Number.isFinite(lon))) {
    try {
      const pos =
        (typeof w.requestInitialLocation === 'function' && await w.requestInitialLocation()) ||
        (typeof w.getUserLocation === 'function' && await w.getUserLocation());
      if (pos) {
        lat = pos.lat ?? pos.latitude ?? pos.coords?.latitude;
        lon = pos.lon ?? pos.longitude ?? pos.coords?.longitude;
      }
    } catch (err) { log.warn('location request failed:', err); }
  }

  const city = readCityOverride() ||
    (typeof w.getUserCityLabel === 'function' ? String(w.getUserCityLabel() || '') : '');

  /** @type {import('../../data/repositories/services.repo.js').ServiceItem[]} */
  let items = [];
  try {
    items = await repo.list(category, { lat, lon, city: city || undefined });
  } catch (err) {
    log.warn('list failed:', err);
  }

  if (items.length === 0) {
    listEl.innerHTML = emptyHtml(category, !!city);
    return;
  }

  // Paginated render — initially show PAGE_SIZE cards plus a "load more"
  // button so the user sees results immediately rather than waiting for the
  // browser to lay out 100+ DOM nodes (and so the screen doesn't feel like
  // "the whole database is being loaded"). The full result list is captured
  // in this closure; each Load More click bumps `shown` and re-renders.
  renderPaged(listEl, items);
}

const PAGE_SIZE = 10;

/**
 * @param {HTMLElement} listEl
 * @param {import('../../data/repositories/services.repo.js').ServiceItem[]} items
 */
function renderPaged(listEl, items) {
  let shown = Math.min(PAGE_SIZE, items.length);
  const repaint = () => {
    const cards = items.slice(0, shown).map((it) => cardHtml(it)).join('');
    const remaining = items.length - shown;
    const more = remaining > 0
      ? `<button type="button" class="svc-load-more">
           טען עוד <strong>${Math.min(PAGE_SIZE, remaining)}</strong>
           <span class="svc-load-more-total">(נשארו ${remaining} מתוך ${items.length})</span>
         </button>`
      : `<div class="svc-list-end">הוצגו כל ${items.length} התוצאות</div>`;
    listEl.innerHTML = cards + more;
    wireListActions(listEl);
    const moreBtn = listEl.querySelector('.svc-load-more');
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        shown = Math.min(shown + PAGE_SIZE, items.length);
        repaint();
        // Scroll the first newly-added card into view so the user sees
        // progress.
        const newCards = listEl.querySelectorAll('.svc-card');
        const target = newCards[shown - PAGE_SIZE];
        target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };
  repaint();
}

// ====================================================================
// internals
// ====================================================================

/** @param {{ icon: string; title: string }} meta */
function headerHtml(meta) {
  return `
    <div class="svc-header">
      <div class="svc-header-icon">${meta.icon}</div>
      <div class="svc-header-title">${escape(meta.title)}</div>
    </div>
  `;
}

function loadingHtml() {
  return `<div class="svc-empty">טוען...</div>`;
}

/**
 * @param {Category} category
 * @param {boolean} hasCity
 */
function emptyHtml(category, hasCity) {
  const needsCity = (category === 'garage' || category === 'inspection') && !hasCity;
  if (needsCity) {
    return `<div class="svc-empty">בחר עיר כדי להציג רשימה (לחיצה על "קבע עיר" למעלה).</div>`;
  }
  return `<div class="svc-empty">לא נמצאו תוצאות באזור הזה.</div>`;
}

/** @param {import('../../data/repositories/services.repo.js').ServiceItem} it */
function cardHtml(it) {
  const distance = Number.isFinite(it.distanceKm)
    ? `<span class="svc-distance">${escape(formatDistance(/** @type {number} */ (it.distanceKm)))}</span>`
    : '';
  const brand = it.brand ? `<span class="svc-brand">${escape(it.brand)}</span>` : '';
  const profession = it.profession ? `<div class="svc-profession">${escape(it.profession)}</div>` : '';
  const addrParts = [it.address, it.city].filter(Boolean).join(', ');
  const addr = addrParts ? `<div class="svc-addr">📍 ${escape(addrParts)}</div>` : '';
  const hours = it.openingHours ? `<div class="svc-hours">🕘 ${escape(it.openingHours)}</div>` : '';
  // Free-form extras (parking capacity/fee, car-wash self-service marker, etc.).
  const extra = it.extra ? `<div class="svc-extra">${escape(it.extra)}</div>` : '';

  const navHref = navUrl(it);
  const phoneHref = it.phone ? `tel:${escape(it.phone)}` : '';

  return `
    <article class="svc-card" data-id="${escape(it.id)}">
      <div class="svc-card-body">
        <div class="svc-card-top">
          <h4 class="svc-name">${escape(it.name)}</h4>
          ${distance}
        </div>
        ${brand}
        ${addr}
        ${profession}
        ${extra}
        ${hours}
      </div>
      <div class="svc-actions">
        ${phoneHref ? `<a class="svc-btn" href="${phoneHref}" aria-label="חיוג">📞 חיוג</a>` : ''}
        ${navHref ? `<a class="svc-btn primary" href="${escape(navHref)}" target="_blank" rel="noopener" aria-label="נווט">🗺️ נווט</a>` : ''}
      </div>
    </article>
  `;
}

/**
 * Build a navigation URL. Prefer coordinates (precise) — fall back to a
 * text search query when only address is known (garages have no lat/lon).
 *
 * @param {import('../../data/repositories/services.repo.js').ServiceItem} it
 * @returns {string}
 */
function navUrl(it) {
  if (Number.isFinite(it.lat) && Number.isFinite(it.lon)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${it.lat},${it.lon}`;
  }
  const q = [it.name, it.address, it.city].filter(Boolean).join(' ');
  if (!q) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/** Wire phone-format fix-up + future card-level actions. @param {HTMLElement} listEl */
function wireListActions(listEl) {
  // Replace raw phone digits with a human-readable display while keeping the
  // tel: href untouched. Cheap, runs once per render.
  listEl.querySelectorAll('a.svc-btn[href^="tel:"]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    const phone = href.replace(/^tel:/, '');
    const pretty = (() => { try { return formatPhone(phone); } catch { return phone; } })();
    a.setAttribute('aria-label', `חיוג ל-${pretty}`);
  });
}

function readCityOverride() {
  try { return String(localStorage.getItem('autome_user_city_override') || ''); }
  catch { return ''; }
}
